// Capital-purpose → document checklist mapping (quiz Step 6 + Onboarding).
// Item ids double as backend upload doc_type keys.

export const CAP_DOC_CATALOG = {
  bank_statements: "6 months bank statements",
  mgmt_accounts: "Latest management accounts (P&L and balance sheet)",
  debtor_creditor_aging: "Debtor and creditor aging report",
  aged_debtors: "Aged debtors report",
  sample_invoices: "Sample invoices",
  proforma_invoice: "Proforma invoice or quotation for the asset",
  asset_valuation_list: "Asset list with current valuations",
  purchase_orders: "Purchase order or confirmed sales contract",
  supplier_invoice: "Supplier invoice or proforma",
  capital_event_evidence: "Evidence of the confirmed capital event (term sheet, award letter, or signed contract)",
  loan_statements: "Current loan statements and repayment schedules",
  audited_accounts: "Audited financials",
  pitch_deck: "Pitch deck",
  financial_model: "Financial model with projections",
  legal: "Company registration documents",
  cashflow_forecast: "Cash flow forecast / financial model",
  asset_register: "Existing asset register",
  insurance_certificate: "Insurance certificate for assets",
  feasibility_study: "Market research or feasibility study",
  board_resolution: "Board resolution authorising the raise",
  import_export_licence: "Import/export licence (where applicable)",
  letter_of_credit: "Letter of credit (if already issued)",
  debtor_insurance: "Debtor insurance policy",
  customer_payment_history: "Customer payment history",
  cap_table: "Cap table or shareholding structure",
  collateral_docs: "Existing security/collateral documentation",
  supporting_docs: "Supporting documents describing the capital need (business plan, projections, contracts)",
};

// "What is this?" explainers per document (info popups in quiz + onboarding).
export const CAP_DOC_INFO = {
  bank_statements: { what: "Official statements from your business bank account covering the last 6 months (PDF or CSV exports from your bank).", why: "Lets funders verify your real cash inflows and outflows — the single fastest way to prove your revenue is real." },
  mgmt_accounts: { what: "Your internal, most recent Profit & Loss statement and balance sheet. Unaudited Excel exports are fine.", why: "Shows funders your current financial position and whether the business is run with proper records." },
  debtor_creditor_aging: { what: "A report listing who owes you money (debtors) and who you owe (creditors), grouped by how overdue each amount is.", why: "Tells working-capital lenders how quickly cash actually moves through your business." },
  aged_debtors: { what: "A breakdown of your unpaid customer invoices grouped by age (30/60/90+ days outstanding).", why: "Invoice financiers advance you cash against these — they need to see quality and age of what's owed to you." },
  sample_invoices: { what: "Two or three real invoices you've issued to customers (redact sensitive details if needed).", why: "Shows financiers the size, format and terms of the receivables they'd be funding." },
  proforma_invoice: { what: "The supplier's quotation or proforma invoice for the exact asset you want to buy — with price, specs and supplier details.", why: "Asset financiers lend against a specific purchase — this document defines exactly what's being financed." },
  asset_valuation_list: { what: "A list of the business's significant assets (equipment, vehicles, property) with their current estimated values.", why: "Shows what security you can offer and how asset-rich the business already is." },
  purchase_orders: { what: "A signed purchase order or confirmed sales contract from your customer for the goods or services being financed.", why: "Trade financiers fund confirmed transactions — this proves the sale is real and committed." },
  supplier_invoice: { what: "The invoice or proforma from your supplier for the goods you need to buy to fulfil the order.", why: "Completes the trade picture: what you must pay out before your customer pays you." },
  capital_event_evidence: { what: "Proof of the confirmed money you're bridging towards — a signed term sheet, award letter, or signed contract.", why: "Bridge lenders need certainty the incoming capital event is real before covering the gap." },
  loan_statements: { what: "Statements and repayment schedules for all your current business loans and facilities.", why: "Refinancing starts with a full picture of what you owe, to whom, and on what terms." },
  audited_accounts: { what: "Annual financial statements reviewed and signed off by an independent, registered auditor.", why: "The strongest trust signal — banks and institutional funders often require them before serious talks." },
  pitch_deck: { what: "A slide deck covering your business, market, team, traction and the capital you're raising.", why: "The fastest way for a funder to understand your business — and the primary input for your Capital Readiness Score." },
  financial_model: { what: "A spreadsheet with your historical numbers and 3–5 year financial projections (revenue, costs, cash flow).", why: "Growth funders back numbers, not just a story — this shows where their capital takes the business." },
  legal: { what: "Your company registration documents — certificate of incorporation, articles of association, business permit or equivalent.", why: "No funder can release money to a business that can't prove it's properly registered. This is mandatory for every application." },
  cashflow_forecast: { what: "A monthly projection of cash coming in and going out over the next 6–12 months — your full financial model works too.", why: "Shows lenders you can service repayments even in tighter months." },
  asset_register: { what: "Your existing record of all business assets with purchase dates and book values.", why: "Demonstrates disciplined asset management and gives financiers a fuller security picture." },
  insurance_certificate: { what: "Proof of insurance covering the asset(s) being financed or offered as security.", why: "Financiers need the asset protected — an uninsured asset is weak security." },
  feasibility_study: { what: "Market research, demand analysis or a feasibility study supporting your expansion plan.", why: "Independent evidence that the market you're expanding into is real reduces funder risk." },
  board_resolution: { what: "A signed resolution from your board or shareholders authorising the company to raise this capital.", why: "Confirms the raise is properly authorised — funders can't sign with a company whose owners haven't approved it." },
  import_export_licence: { what: "Your current import or export licence, where your trade transaction crosses borders.", why: "Proves you're legally allowed to complete the trade being financed." },
  letter_of_credit: { what: "A letter of credit already issued by a bank in connection with your trade transaction, if you have one.", why: "An existing LC significantly de-risks the transaction for additional financiers." },
  debtor_insurance: { what: "A credit-insurance policy covering non-payment by your customers, if you hold one.", why: "Insured receivables get better advance rates — the financier's risk drops sharply." },
  customer_payment_history: { what: "A simple record showing how promptly your key customers have paid past invoices.", why: "Reliable payers make your invoices much easier (and cheaper) to finance." },
  cap_table: { what: "A table showing who owns what share of the company, including any convertible instruments.", why: "Funders need to know exactly who they're dealing with and how ownership is structured." },
  collateral_docs: { what: "Documentation for any security or collateral already pledged on existing loans (charges, debentures, title documents).", why: "Refinanciers must know what's already pledged before structuring new terms." },
  supporting_docs: { what: "Any documents that best describe your capital need — a business plan, projections, or signed contracts.", why: "Helps our team route your unusual capital need to the right funder pool." },
};

// Estimated CRS points a founder can gain by uploading each document
// ("up to" — exact gain is computed when the analysis re-runs). Derived
// from the scoring rubric weights (revenue verification, DRS/ERS/RBFRS
// gates, financial-maturity checks).
export const CAP_DOC_BOOST = {
  bank_statements: 8,
  legal: 5,
  mgmt_accounts: 7,
  audited_accounts: 8,
  pitch_deck: 6,
  financial_model: 7,
  purchase_orders: 5,
  capital_event_evidence: 5,
  cap_table: 5,
  debtor_creditor_aging: 4,
  aged_debtors: 4,
  proforma_invoice: 4,
  asset_valuation_list: 4,
  loan_statements: 4,
  cashflow_forecast: 4,
  feasibility_study: 4,
  sample_invoices: 3,
  supplier_invoice: 3,
  letter_of_credit: 3,
  debtor_insurance: 3,
  customer_payment_history: 3,
  asset_register: 3,
  collateral_docs: 3,
  supporting_docs: 3,
  insurance_certificate: 2,
  board_resolution: 2,
  import_export_licence: 2,
};

// Per capital-purpose document lists (ids reference CAP_DOC_CATALOG).
export const PURPOSE_DOC_MAP = {
  working_capital: {
    required: ["bank_statements", "mgmt_accounts", "legal"],
    optional: ["audited_accounts"],
  },
  asset_finance: {
    required: ["proforma_invoice", "asset_valuation_list", "bank_statements", "mgmt_accounts", "legal"],
    optional: ["asset_register", "insurance_certificate"],
  },
  growth_capital: {
    required: ["audited_accounts", "pitch_deck", "financial_model", "mgmt_accounts", "legal"],
    optional: ["bank_statements", "feasibility_study", "board_resolution"],
  },
  trade_finance: {
    required: ["purchase_orders", "supplier_invoice", "bank_statements", "mgmt_accounts", "legal"],
    optional: ["import_export_licence", "letter_of_credit"],
  },
  invoice_financing: {
    required: ["aged_debtors", "sample_invoices", "bank_statements", "mgmt_accounts", "legal"],
    optional: ["debtor_insurance", "customer_payment_history"],
  },
  bridge_financing: {
    required: ["capital_event_evidence", "bank_statements", "mgmt_accounts", "legal"],
    optional: ["cap_table"],
  },
  debt_refinancing: {
    required: ["loan_statements", "bank_statements", "mgmt_accounts", "legal"],
    optional: ["audited_accounts", "collateral_docs"],
  },
  other: {
    required: ["mgmt_accounts", "pitch_deck", "financial_model", "legal"],
    optional: ["bank_statements", "supporting_docs"],
  },
};

// Docs Clinq's advisory team can help founders build from scratch.
export const CLINQ_CAN_BUILD = new Set(["pitch_deck", "financial_model"]);
export const checklistHasBuildableDocs = (items) =>
  (items || []).some((i) => CLINQ_CAN_BUILD.has(i.id));

// Merge the lists for all selected purposes, dedupe, Required beats
// Optional; Required items first, then Optional (catalog order within each).
export function buildChecklist(purposes) {
  const req = new Set();
  const opt = new Set();
  (purposes || []).forEach((p) => {
    const m = PURPOSE_DOC_MAP[p];
    if (!m) return;
    m.required.forEach((id) => req.add(id));
    m.optional.forEach((id) => opt.add(id));
  });
  req.forEach((id) => opt.delete(id));
  const order = Object.keys(CAP_DOC_CATALOG);
  const rows = (set, requirement) =>
    order.filter((id) => set.has(id))
      .map((id) => ({ id, label: CAP_DOC_CATALOG[id], requirement }));
  return [...rows(req, "required"), ...rows(opt, "optional")];
}
